import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, RotateCcw, Sparkles, FileJson } from 'lucide-react';
import { OpenRouterModelPicker } from '../components/OpenRouterModelPicker';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminScorecardConfig = () => {
  const [cfg, setCfg] = useState({ system_prompt: '', model: '' });
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSchema, setShowSchema] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          axios.get(`${API}/api/scorecard/admin/config`),
          axios.get(`${API}/api/scorecard/admin/defaults`),
        ]);
        setCfg({
          system_prompt: a.data.system_prompt || '',
          model: a.data.model || b.data.default_model,
        });
        setDefaults(b.data);
      } catch (e) {
        toast.error('載入設定失敗');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/scorecard/admin/config`, cfg);
      toast.success('已儲存');
    } catch (e) {
      toast.error('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const restore = () => {
    if (!defaults) return;
    setCfg({
      system_prompt: defaults.default_system_prompt,
      model: defaults.default_model,
    });
    toast.info('已還原為預設（記得點儲存）');
  };

  if (loading) {
    return (
      <main className="flex-1 overflow-y-auto p-10 text-sm text-neutral-500">載入中...</main>
    );
  }

  return (
    <main className="flex-1 overflow-y-auto p-4 md:p-8" data-testid="admin-scorecard-config-root">
      <div className="max-w-5xl mx-auto">
      <header className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-primary-500" strokeWidth={2} />
          <span className="label-micro text-primary-500">SCORECARD · CONFIG</span>
        </div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-neutral-900 mb-2">
          影片診斷報告設定
        </h1>
        <p className="text-sm text-neutral-500 max-w-2xl">
          控制 AI 如何分析用戶上傳的 IG 後台截圖 + 影片連結，並產出結構化 JSON 報告。
          <strong className="text-red-500"> 注意：</strong>
          系統會自動在你的 Prompt 後面附加 JSON Schema 規範，確保 AI 輸出可被前端解析。你不需要（也不應該）自己寫 schema。
        </p>
      </header>

      <div className="space-y-6">
        {/* Model */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <label className="block text-xs font-semibold text-neutral-700 mb-2 tracking-wider">
            MODEL · 模型（OpenRouter）
          </label>
          <OpenRouterModelPicker
            value={cfg.model || ''}
            onChange={(v) => setCfg({ ...cfg, model: v })}
            mode="text"
            defaultModel={defaults?.default_model}
            testIdPrefix="scorecard-config-model"
          />
          <p className="text-xs text-neutral-500 mt-2">
            Scorecard 需要支援 vision 嘅模型。推薦：Opus 4.7 / 4.5 / Sonnet 4.6。
          </p>
        </div>

        {/* System Prompt */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs font-semibold text-neutral-700 tracking-wider">
              SYSTEM · PROMPT · 主提示詞
            </label>
            <button
              onClick={() => setShowSchema((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700"
            >
              <FileJson className="w-3.5 h-3.5" />
              {showSchema ? '收起 Schema' : '查看系統附加的 JSON Schema'}
            </button>
          </div>
          <textarea
            value={cfg.system_prompt}
            onChange={(e) => setCfg({ ...cfg, system_prompt: e.target.value })}
            rows={18}
            className="w-full bg-white border border-neutral-300 focus:border-primary-500 rounded-lg px-4 py-3 text-sm leading-relaxed font-mono outline-none resize-y"
            data-testid="scorecard-config-prompt"
          />
          <p className="text-xs text-neutral-500 mt-2">
            定義 AI 的角色、語氣、分析深度、語言。系統會自動在最後附加 JSON 結構規範，無需手寫。
          </p>
        </div>

        {/* Schema preview */}
        {showSchema && defaults?.schema_appendix && (
          <div className="bg-neutral-900 text-neutral-100 rounded-xl p-5 overflow-x-auto">
            <div className="text-xs tracking-[0.2em] text-primary-500 mb-3 font-semibold">
              AUTO · APPENDED · SCHEMA（唯讀）
            </div>
            <pre className="text-[11px] leading-relaxed whitespace-pre-wrap font-mono">
              {defaults.schema_appendix}
            </pre>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            onClick={restore}
            className="inline-flex items-center gap-2 border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 px-4 h-10 rounded-lg text-sm"
            data-testid="scorecard-config-restore"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            還原預設
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-5 h-10 rounded-lg text-sm font-semibold disabled:opacity-50"
            data-testid="scorecard-config-save"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? '儲存中...' : '儲存設定'}
          </button>
        </div>
      </div>
      </div>
    </main>
  );
};

export default AdminScorecardConfig;
